import { useState } from "react";
import { createListStore, getFilterDto, getSort } from "../../common/listStore";
import { PageTitle } from "../../layout/PageTitle";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Button, Grid } from "@mui/material";
import { createEntityStore } from "../../common/entityStore";
import { today } from "../../common/utils";
import { propValidValue } from "../../common/entityStoreValidators";
import { propDateField, propEnumField } from "../../common/entityStoreForm";
import { downloadFromApiPost } from "../../common/download";
import { applyListStoreFilters, nextDay } from "../../common/listStoreFilters";
import { useArticlesWithCode, useTransactionCodesWithCode, useWarehouses } from "../../common/enum";

export function InventoryTransactionsReportPage() {
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<InventoryTransactionDto>({
            path: "reports/inventoryTransactions/list",
            supportsApiFilterSortPage: true,
            itemsPerPage: 15,
            listProps: [
                { column: "trnDte", label: "Transaction date", type: "date", width: 120 },
                { column: "trnCde", label: "Transaction code", width: 150 },
                { column: "invId", label: "Article ID", width: 150 },
                { column: "fromWh", label: "From", width: 150 },
                { column: "toWh", label: "To", width: 150 },
                { column: "trnQty", label: "Quantity", width: 150, type: "number" },
            ]
        });
        const useFiltersStore = createEntityStore<PaymentErrorListFilters>({
            data: getFiltersStoreInitialData(), isValidationEnabled: true,
            propValidators: [
                propValidValue("from", "fromFormatted"),
                propValidValue("to", "toFormatted"),
            ]
        });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["trnDte"], operator: "GreaterThanOrEqual", filterProp: "from" },
            { listProps: ["trnDte"], operator: "LessThan", filterProp: "to", transform: nextDay },
            { listProps: ["transactionCodeId"], operator: "Equal", filterProp: "transactionCodeId" },
            { listProps: ["inventoryArticleId"], operator: "Equal", filterProp: "inventoryArticleId" },
            { listProps: ["fromWarehouseId", "toWarehouseId"], operator: "Equal", filterProp: "warehouseId" },
        ]);
        return { useStore, useFiltersStore };
    });

    function clearFiltersStore() {
        useFiltersStore.getState().setClientData(getFiltersStoreInitialData());
    }

    const { isFetching } = useStore(p => ({ isFetching: p.fetching }));

    return <>
        <PageTitle title="Transaction report" />
        {renderListStore(useStore, {
            header: listHeaderItems([
                ["export", () => <Button disabled={isFetching} onClick={() => downloadFromApiPost(`reports/inventoryTransactions/xlsx`, {
                    filter: getFilterDto(useStore.getState()),
                    sort: getSort(useStore.getState()),
                })}>Export</Button>],
            ]),
            filtersMaxWidth: "xl",
            renderExternalFilters: () => <>
                <Grid item xs={4} md={1.5}>{propDateField(useFiltersStore)("from", "fromFormatted", "Date from")}</Grid>
                <Grid item xs={4} md={1.5}>{propDateField(useFiltersStore)("to", "toFormatted", "Date to")}</Grid>
                <Grid item xs={4} md={2.5}>{propEnumField(useFiltersStore)("transactionCodeId", "Transaction code", useTransactionCodesWithCode, { addAll: true })}</Grid>
                <Grid item xs={4} md={3.5}>{propEnumField(useFiltersStore)("inventoryArticleId", "Inventory article", useArticlesWithCode, { addAll: true })}</Grid>
                <Grid item xs={4} md={1.5} lg={1}>{propEnumField(useFiltersStore)("warehouseId", "Warehouse", useWarehouses, { addAll: true })}</Grid>
            </>, clearExternalFilters: clearFiltersStore,
        })};
    </>
}

export interface InventoryTransactionDto {
    id: number;
    transactionCodeId: number;
    trnCde: string;
    trnDte: string;
    inventoryArticleId: number;
    invId: string;
    trnQty: number;
    fromWarehouseId: number;
    fromWh: string;
    toWarehouseId: number;
    toWh: string;
}

interface PaymentErrorListFilters {
    from?: string;
    to?: string;
    fromFormatted?: string;
    toFormatted?: string;
    transactionCodeId?: number;
    inventoryArticleId?: number;
    warehouseId?: number;
}

function getFiltersStoreInitialData() {
    return { from: today(), to: today() };
}
